/**
 * Explicit per-row split allocations for the CSV converter.
 *
 * A split is a reader's edit to **one** prepared row: instead of the single
 * other-leg account, the row's source posting is balanced by two or more
 * contra postings, each a positive decimal magnitude to a destination
 * account. The model is deliberately narrow (a first slice):
 *
 * - **Keyed to the current source row.** A split names a 0-based data-row
 *   index *and* the {@link CsvRowSplit.sourceKey} of the row it was made
 *   for. A different file, a re-sorted paste or a changed mapping yields a
 *   different key, so the edit is reported `stale_row` rather than silently
 *   following a row it was never made for.
 * - **Exact arithmetic, never epsilon.** Magnitudes are compared as scaled
 *   integers against the source posting's emitted `toFixed` text — the same
 *   discipline `csv-statement-reconciliation.ts` applies. An allocation with
 *   more significant decimals than the source posting is rejected
 *   (`unsupported_precision`), never rounded into agreement.
 * - **Same currency, positive magnitudes, opposite sign.** Every allocation
 *   is > 0; the emitter writes it with the sign opposite to the source
 *   posting, so `-100.00` balances as `+60.00` / `+40.00` and a `+25.00`
 *   refund as `-15.00` / `-10.00`. No FX, no mixed-sign netting.
 * - **Never the source account.** A destination equal to the source account
 *   would cancel the bank movement; unsplit same-account rows keep their
 *   existing behavior, but a split refuses it.
 *
 * Pure and dependency-light: no import from `csv-to-beancount` at runtime,
 * so the converter can depend on this module without a cycle.
 */

import { isValidAccount } from "@/lib/beancount-syntax";
import {
  formatScaled,
  parseFixedText,
  toScaledMagnitude,
} from "@/lib/fixed-point-text";

/** Upper bound on allocations per split row — the model is bounded by design. */
export const MAX_CSV_SPLIT_ALLOCATIONS = 10;

/** Minimum nonempty allocations that make a row a split. */
export const MIN_CSV_SPLIT_ALLOCATIONS = 2;

/** One reader-entered allocation: destination account + decimal magnitude text. */
export interface CsvSplitAllocation {
  account: string;
  /** Plain positive decimal text (`60`, `60.00`, `0.5`); no sign, no grouping. */
  amountText: string;
}

/** A reader's split edit for one data row of the current source file. */
export interface CsvRowSplit {
  /** 0-based index into the data `rows` array (same as `PreparedCsvRow.rowIndex`). */
  rowIndex: number;
  /**
   * Identity of the row the split was made for — `preparedRowSplitKey(row)`
   * from `csv-to-beancount`. A mismatch means the source changed underneath.
   */
  sourceKey: string;
  allocations: readonly CsvSplitAllocation[];
}

/**
 * One validated contra posting, already signed opposite to the source
 * posting and written at the source posting's precision.
 */
export interface CsvResolvedAllocation {
  account: string;
  /** Signed fixed-point text as emitted, e.g. `60.00` for a `-100.00` source. */
  amountText: string;
}

/** A split that passed validation; attached to its prepared row. */
export interface CsvResolvedSplit {
  allocations: readonly CsvResolvedAllocation[];
}

/**
 * Why a split cannot be applied. `index` is the 0-based position in the
 * reader's `allocations` array (blank entries keep their position), so a UI
 * can point at the exact field.
 */
export type CsvSplitIssue =
  | { code: "unknown_row" }
  | { code: "row_unavailable" }
  | { code: "stale_row" }
  | { code: "too_few_allocations"; count: number; min: number }
  | { code: "too_many_allocations"; count: number; max: number }
  | { code: "missing_account"; index: number }
  | { code: "missing_amount"; index: number }
  | { code: "invalid_account"; index: number; account: string }
  | { code: "source_account"; index: number; account: string }
  | { code: "invalid_amount"; index: number; amountText: string }
  | { code: "nonpositive_amount"; index: number; amountText: string }
  | {
      code: "unsupported_precision";
      index: number;
      amountText: string;
      maxDecimals: number;
    }
  | {
      code: "sum_mismatch";
      direction: "over" | "under";
      /** Absolute source posting magnitude, e.g. `100.00`. */
      expectedText: string;
      /** Sum of the valid allocations at source precision. */
      allocatedText: string;
      /** `expected - allocated`, signed (negative when over-allocated). */
      remainingText: string;
    };

/** Issue codes that belong to an allocation's account field (the rest are its amount's). */
export const CSV_SPLIT_ACCOUNT_ISSUE_CODES: ReadonlySet<CsvSplitIssue["code"]> =
  new Set(["missing_account", "invalid_account", "source_account"]);

/** Every issue for one row's split. Never empty. */
export interface CsvSplitDiagnostic {
  rowIndex: number;
  /** 1-based file line when the row exists; absent for `unknown_row`. */
  line?: number;
  issues: readonly CsvSplitIssue[];
}

/** What the split validator needs to know about the row being split. */
interface CsvSplitSourceRow {
  /** Current identity of the row; compared to {@link CsvRowSplit.sourceKey}. */
  sourceKey: string;
  /** The source posting's emitted fixed-point text (`preparedAmountText`). */
  amountText: string;
}

type CsvSplitResolution =
  | { ok: true; split: CsvResolvedSplit }
  | { ok: false; issues: CsvSplitIssue[] };

function isBlank(allocation: CsvSplitAllocation): boolean {
  return allocation.account.trim() === "" && allocation.amountText.trim() === "";
}

/**
 * Validate one split against the row it targets and, when valid, produce the
 * signed contra postings the emitter writes. Every issue is reported at once
 * (not first-failure) so a reader can fix the whole row in one pass.
 */
export function resolveCsvRowSplit(
  split: CsvRowSplit,
  row: CsvSplitSourceRow,
  sourceAccount: string,
): CsvSplitResolution {
  if (split.sourceKey !== row.sourceKey) {
    return { ok: false, issues: [{ code: "stale_row" }] };
  }
  const source = parseFixedText(row.amountText);
  if (source === null) {
    // The emitter always writes plain fixed text; anything else is not splittable.
    return { ok: false, issues: [{ code: "row_unavailable" }] };
  }
  const scale = source.frac.length;
  const expected = toScaledMagnitude(source, scale)!;
  const sourceName = sourceAccount.trim();

  const issues: CsvSplitIssue[] = [];
  const valid: { account: string; scaled: bigint }[] = [];
  let nonempty = 0;

  split.allocations.forEach((allocation, index) => {
    if (isBlank(allocation)) return;
    nonempty += 1;
    const account = allocation.account.trim();
    const amountText = allocation.amountText.trim();
    let accountOk = false;
    if (account === "") {
      issues.push({ code: "missing_account", index });
    } else if (!isValidAccount(account)) {
      issues.push({ code: "invalid_account", index, account });
    } else if (account === sourceName) {
      issues.push({ code: "source_account", index, account });
    } else {
      accountOk = true;
    }

    if (amountText === "") {
      issues.push({ code: "missing_amount", index });
      return;
    }
    const parsed = parseFixedText(amountText);
    if (parsed === null) {
      issues.push({ code: "invalid_amount", index, amountText });
      return;
    }
    const magnitude = toScaledMagnitude(parsed, scale);
    if (magnitude === null) {
      issues.push({
        code: "unsupported_precision",
        index,
        amountText,
        maxDecimals: scale,
      });
      return;
    }
    if (parsed.negative || magnitude === 0n) {
      issues.push({ code: "nonpositive_amount", index, amountText });
      return;
    }
    if (accountOk) valid.push({ account, scaled: magnitude });
  });

  if (nonempty < MIN_CSV_SPLIT_ALLOCATIONS) {
    issues.unshift({
      code: "too_few_allocations",
      count: nonempty,
      min: MIN_CSV_SPLIT_ALLOCATIONS,
    });
  } else if (nonempty > MAX_CSV_SPLIT_ALLOCATIONS) {
    issues.unshift({
      code: "too_many_allocations",
      count: nonempty,
      max: MAX_CSV_SPLIT_ALLOCATIONS,
    });
  }

  // The balance check is only meaningful once every entry parsed; a sum over
  // a partial list would report a mismatch the reader has not made yet.
  if (issues.length === 0) {
    const allocated = valid.reduce((sum, a) => sum + a.scaled, 0n);
    if (allocated !== expected) {
      issues.push({
        code: "sum_mismatch",
        direction: allocated > expected ? "over" : "under",
        expectedText: formatScaled(expected, scale),
        allocatedText: formatScaled(allocated, scale),
        remainingText: formatScaled(expected - allocated, scale),
      });
    }
  }

  if (issues.length > 0) return { ok: false, issues };

  // Contra postings carry the sign opposite to the source posting. A zero
  // source cannot reach here: positive allocations never sum to zero.
  const contraNegative = !source.negative;
  return {
    ok: true,
    split: {
      allocations: valid.map(({ account, scaled }) => ({
        account,
        amountText: formatScaled(contraNegative ? -scaled : scaled, scale),
      })),
    },
  };
}

/** Live balance of a split being edited, at the source posting's precision. */
interface CsvSplitBalance {
  /** Absolute source posting magnitude, e.g. `100.00`. */
  expectedText: string;
  /** Sum of the allocations that currently parse as positive in-precision amounts. */
  allocatedText: string;
  /** `expected - allocated`, signed (negative when over-allocated). */
  remainingText: string;
  /** `0` balanced, `1` under-allocated, `-1` over-allocated. */
  sign: -1 | 0 | 1;
}

/**
 * The running total a split editor shows while the reader types. Unlike
 * {@link resolveCsvRowSplit} it never refuses: an amount that does not (yet)
 * parse, is not positive or carries too many decimals simply contributes
 * nothing, so the remaining figure moves with every valid keystroke. It is a
 * display aid, never a validity verdict — only the resolver decides whether
 * a split applies. `null` when the source text is not plain fixed-point.
 */
export function csvSplitBalance(
  allocations: readonly CsvSplitAllocation[],
  sourceAmountText: string,
): CsvSplitBalance | null {
  const source = parseFixedText(sourceAmountText);
  if (source === null) return null;
  const scale = source.frac.length;
  const expected = toScaledMagnitude(source, scale)!;
  let allocated = 0n;
  for (const allocation of allocations) {
    const parsed = parseFixedText(allocation.amountText);
    if (parsed === null || parsed.negative) continue;
    const magnitude = toScaledMagnitude(parsed, scale);
    if (magnitude !== null) allocated += magnitude;
  }
  const remaining = expected - allocated;
  return {
    expectedText: formatScaled(expected, scale),
    allocatedText: formatScaled(allocated, scale),
    remainingText: formatScaled(remaining, scale),
    sign: remaining === 0n ? 0 : remaining > 0n ? 1 : -1,
  };
}

/**
 * Normalize the option into one split per row index. When a caller passes two
 * splits for the same row, the later one wins — the list is a reader's edit
 * history flattened, not a set of alternatives.
 */
export function indexCsvRowSplits(
  splits: readonly CsvRowSplit[] | undefined,
): Map<number, CsvRowSplit> {
  const byRow = new Map<number, CsvRowSplit>();
  for (const split of splits ?? []) byRow.set(split.rowIndex, split);
  return byRow;
}

/** Prefix of every {@link csvSplitIssueReason}, so a UI can recognise split row errors. */
export const CSV_SPLIT_REASON_PREFIX = "split: ";

/**
 * Short English reason for a {@link RowError}-style diagnostic line. The UI
 * localizes from the typed issue instead; this keeps the converter's
 * `errors` array self-describing for tests and non-UI callers.
 */
export function csvSplitIssueReason(issue: CsvSplitIssue): string {
  return `${CSV_SPLIT_REASON_PREFIX}${splitIssueDetail(issue)}`;
}

function splitIssueDetail(issue: CsvSplitIssue): string {
  switch (issue.code) {
    case "unknown_row":
      return "no such row";
    case "row_unavailable":
      return "row cannot be converted";
    case "stale_row":
      return "source row changed";
    case "too_few_allocations":
      return `needs at least ${issue.min} allocations (has ${issue.count})`;
    case "too_many_allocations":
      return `at most ${issue.max} allocations (has ${issue.count})`;
    case "missing_account":
      return `allocation ${issue.index + 1} has no account`;
    case "missing_amount":
      return `allocation ${issue.index + 1} has no amount`;
    case "invalid_account":
      return `invalid account: ${issue.account}`;
    case "source_account":
      return `allocation ${issue.index + 1} posts to the source account`;
    case "invalid_amount":
      return `invalid amount: ${issue.amountText}`;
    case "nonpositive_amount":
      return `amount must be positive: ${issue.amountText}`;
    case "unsupported_precision":
      return `amount ${issue.amountText} has more than ${issue.maxDecimals} decimals`;
    case "sum_mismatch":
      return `allocations total ${issue.allocatedText}, expected ${issue.expectedText}`;
  }
}
